import { Injectable } from '@angular/core';
import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpParams,
	HttpRequest,
	HttpResponse,
} from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
	attachMockEthernetGateway,
	attachMockEthernetGatewayToSites,
	isMockEthernetGatewayId,
	resolveMockEthernetGatewayRequest,
	startMockAutomFwUpdateSimulation,
} from '../conf/mock-ethernet-gateway';
import { AppState } from '../store/app.reducer';
import * as DeviceActions from '../store/device/device.actions';

@Injectable()
export class MockEthernetGatewayInterceptor implements HttpInterceptor {
	constructor(private store: Store<AppState>) { }

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const resolution = resolveMockEthernetGatewayRequest(
			req.method,
			req.urlWithParams || req.url,
			req.params.get('devices')
		);
		if (!resolution) {
			return next.handle(req);
		}
		if (resolution.kind === 'respond') {
			if (req.method === 'POST' && /\/v1\/devices\/\d+\/updates/.test(req.urlWithParams || req.url)) {
				const body = req.body as { Component?: string } | null;
				startMockAutomFwUpdateSimulation(body?.Component, (deviceId, event) => {
					this.store.dispatch(DeviceActions.deviceStateUpdate({ deviceId, event }));
				});
			}
			return this.respond(resolution.body);
		}
		if (resolution.kind === 'attach-sites') {
			return next.handle(req).pipe(
				map(event => {
					if (!(event instanceof HttpResponse)) {
						return event;
					}
					const body = event.body || {};
					if (Array.isArray(body.Data)) {
						return event.clone({ body: { ...body, Data: attachMockEthernetGatewayToSites(body.Data) } });
					}
					if (body.Data?.Id) {
						return event.clone({ body: { ...body, Data: attachMockEthernetGateway(body.Data) } });
					}
					if (body.Success && (!body.Data || (Array.isArray(body.Data) && body.Data.length === 0))) {
						return event.clone({ body: { ...body, Data: attachMockEthernetGatewayToSites([]) } });
					}
					return event;
				})
			);
		}
		const stripped = req.clone({
			params: new HttpParams().set('devices', '[' + resolution.realIds.toString() + ']'),
		});
		return next.handle(stripped).pipe(
			map(event => {
				if (event instanceof HttpResponse) {
					const body = event.body || {};
					return event.clone({
						body: {
							...body,
							Success: body.Success !== false,
							Data: [...(body.Data || []), ...resolution.mockStatuses],
						},
					});
				}
				return event;
			}),
			catchError(() => of(new HttpResponse({
				status: 200,
				body: { Success: true, Data: resolution.mockStatuses },
			})))
		);
	}

	private respond(body: any): Observable<HttpEvent<unknown>> {
		const device = body?.Data;
		if (!device || !isMockEthernetGatewayId(device.Id) || device.ModelId == null) {
			return of(new HttpResponse({ status: 200, body }));
		}
		return this.store.select('user', 'currentUser').pipe(
			take(1),
			map(user => new HttpResponse({
				status: 200,
				body: {
					...body,
					Data: { ...device, OwnerId: user?.Id ?? device.OwnerId },
				},
			}))
		);
	}
}
